// UI pré-demo 05/07/2026 - lista compacta de divergências reaproveitada em 3 lugares:
// aba "Crítica CCV" e bloco "Pendências da operação" (OperacaoModal), painel por esteira
// (SubBoard). Mesmos dados/endpoint/handlers da Fila de Divergências (pages/Divergencias.tsx)
// - só a apresentação é mais compacta; aprovar/rejeitar chamam o mesmo divergenciasApi.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { divergenciasApi, operacoesApi, type ErroISolve } from '../services/api';
import { toastErroISolve } from '../utils/toastErroISolve';
import type { DivergenciaConector } from '../types';

// Cobre também os tipos ainda não mapeados em TIPO_DIVERGENCIA_LABEL (CRITICA_CCV,
// DOIS_TEMPOS_*) - ver observação do reconhecimento 05/07: o Record global só cobre os
// 3 tipos originais do conector incremental.
const TIPO_LABEL: Record<string, string> = {
  INATIVACAO_COMPRADOR:      'Inativação',
  DIVERGENCIA_CAMPO:         'Campo divergente',
  REGISTRO_NOVO:             'Registro novo',
  CRITICA_CCV:               'Crítica CCV',
  DOIS_TEMPOS_ASSINATURA:    'Dois tempos - assinatura',
  DOIS_TEMPOS_ALTO_PADRAO:   'Dois tempos - alto padrão',
  DOIS_TEMPOS_VEDACAO_LIMITE: 'Dois tempos - vedação/limite',
};

const TIPO_COR: Record<string, string> = {
  INATIVACAO_COMPRADOR: 'bg-red-100 text-red-700',
  DIVERGENCIA_CAMPO:    'bg-amber-100 text-amber-700',
  REGISTRO_NOVO:        'bg-blue-100 text-blue-700',
  CRITICA_CCV:          'bg-purple-100 text-purple-700',
};
const TIPO_COR_PADRAO = 'bg-indigo-100 text-indigo-700'; // DOIS_TEMPOS_*

interface DivergenciasMiniProps {
  divergencias: DivergenciaConector[];
  onResolvido: (id: string) => void;
  vazio?: string;
  mostrarOperacao?: boolean;
  // Rótulos dos botões — no contexto da Crítica CCV usamos "Aceitar/Recusar"
  // (aceitar = o CCV vira a fonte e o valor é gravado no cadastro); nos demais
  // usos segue "Aprovar/Rejeitar". Não muda o mecanismo, só a legenda.
  labelAprovar?: string;
  labelRejeitar?: string;
}

export default function DivergenciasMini({ divergencias, onResolvido, vazio, mostrarOperacao, labelAprovar = 'Aprovar', labelRejeitar = 'Rejeitar' }: DivergenciasMiniProps) {
  const [resolvendo, setResolvendo] = useState<Record<string, 'aprovando' | 'rejeitando'>>({});
  const navigate = useNavigate();

  async function refletirDivergencia(d: DivergenciaConector) {
    const operacaoId = d.operacaoId!;
    const escopo: 'PARTE' | 'IMOVEL' | 'OPERACAO' | 'TUDO' =
      d.entidade === 'Parte'    ? 'PARTE'
      : d.entidade === 'Imovel' ? 'IMOVEL'
      : d.entidade === 'Operacao' ? 'OPERACAO'
      : 'TUDO';
    try {
      const r = await operacoesApi.refletirNoIsolve(operacaoId, {
        escopo,
        parteId: d.entidade === 'Parte' ? d.entidadeId : undefined,
      });
      if (r.ok) toast.success(`Replicado no iSolve (${r.camposAplicados ?? 0} campo(s)).`);
      else toast.warning(r.motivo ?? 'Não replicado no iSolve.');
    } catch (e: any) {
      const erroDiscriminador = e?.response?.data?.erro as ErroISolve | undefined;
      if (erroDiscriminador) toastErroISolve(erroDiscriminador, navigate);
      else toast.error('Falha ao refletir no iSolve.');
    }
  }

  async function resolver(id: string, acao: 'APROVAR' | 'REJEITAR') {
    setResolvendo((r) => ({ ...r, [id]: acao === 'APROVAR' ? 'aprovando' : 'rejeitando' }));
    try {
      if (acao === 'APROVAR') {
        await divergenciasApi.aprovar(id);
        onResolvido(id);
        // Write-back in-screen (toast com ação), não window.confirm. Só quando a
        // operação foi resolvida - escopo derivado da entidade corrigida.
        const d = divergencias.find((x) => x.id === id);
        if (d?.operacaoId) {
          toast('Correção aceita.', {
            description: 'Deseja refletir esta alteração no iSolve?',
            action: { label: 'Refletir', onClick: () => refletirDivergencia(d) },
            duration: 8000,
          });
        }
      } else {
        await divergenciasApi.rejeitar(id);
        onResolvido(id);
      }
    } catch (e: any) {
      toast.error(`Erro ao resolver: ${e?.response?.data?.erro ?? e.message}`);
    } finally {
      setResolvendo((r) => { const c = { ...r }; delete c[id]; return c; });
    }
  }

  if (divergencias.length === 0) {
    return vazio ? <p className="text-xs text-slate-400">{vazio}</p> : null;
  }

  return (
    <div className="space-y-1.5">
      {divergencias.map((d) => (
        <div key={d.id} className="flex items-center gap-2 text-xs border border-slate-200 rounded-lg px-2.5 py-2 bg-white">
          <span className={`shrink-0 px-1.5 py-0.5 rounded font-semibold ${TIPO_COR[d.tipo] ?? TIPO_COR_PADRAO}`}>
            {TIPO_LABEL[d.tipo] ?? d.tipo}
          </span>
          <div className="flex-1 min-w-0">
            {mostrarOperacao && (
              <p className="text-[11px] text-slate-400 truncate">
                {d.operacaoNumero ?? 'operação não identificada'}
                {d.compradorPrincipalNome ? ` · ${d.compradorPrincipalNome}` : ''}
              </p>
            )}
            <p className="text-slate-700 truncate">
              <span className="font-mono text-slate-500">{d.campo}</span>
              {': '}
              <span className="text-slate-400">{d.valorAtualCRM ?? '-'}</span>
              {' → '}
              <span className="font-medium text-slate-800">{d.valorPropostoISolve ?? '-'}</span>
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            <button
              type="button"
              onClick={() => resolver(d.id, 'APROVAR')}
              disabled={!!resolvendo[d.id]}
              className="text-[11px] bg-emerald-600 text-white px-2 py-1 rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {resolvendo[d.id] === 'aprovando' ? '…' : labelAprovar}
            </button>
            <button
              type="button"
              onClick={() => resolver(d.id, 'REJEITAR')}
              disabled={!!resolvendo[d.id]}
              className="text-[11px] bg-slate-200 text-slate-700 px-2 py-1 rounded hover:bg-slate-300 disabled:opacity-50"
            >
              {resolvendo[d.id] === 'rejeitando' ? '…' : labelRejeitar}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
